import React, { useState, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

// Define types for the image objects
interface Image {
	image: string
	title: string
	description: string
}

const Gallery: React.FC = () => {
	const { ref: h2Ref, inView: h2InView } = useInView({
		triggerOnce: true,
		threshold: 0.5,
	})

	const images: Image[] = [
		{
			image: 'gameplay.png',
			title: 'Retaking Bomb B',
			description: 'A Blue Team member trying to re-take bomb B.',
		},
		{
			image: 'gameplay_2.png',
			title: 'Pre-fire is King',
			description: 'A Blue Team member pre-firing while picking a corner.',
		},
		{
			image: 'gameplay_3.png',
			title: 'Molotov Cross',
			description: 'A Red Team member being shot while crossing a molotov.',
		},
		{
			image: 'gameplay_4.png',
			title: 'Retaking Bomb A',
			description: 'A Blue Team member trying to re-take bomb A.',
		},
	]

	// State variables with proper types
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
	const [selectedImage, setSelectedImage] = useState<Image | null>(null)

	// Open modal with the selected image
	const openModal = (image: Image): void => {
		setSelectedImage(image)
		setIsModalOpen(true)
	}

	// Close the modal
	const closeModal = (): void => {
		setIsModalOpen(false)
		setSelectedImage(null)
	}

	// Lock scrolling when modal is open
	useEffect(() => {
		if (isModalOpen) {
			document.body.style.overflow = 'hidden' // Lock scroll
		} else {
			document.body.style.overflow = 'auto' // Enable scroll
		}
		return () => {
			document.body.style.overflow = 'auto' // Cleanup on unmount
		}
	}, [isModalOpen])

	return (
		<div className="p-8 z-10">
			<h2
				ref={h2Ref}
				className={`text-6xl font-bold text-center mt-8 mb-8 text-white tiny5 ${
					h2InView ? 'animate-title' : 'opacity-0 visibility-hidden'
				}`}
			>
				GALLERY
			</h2>

			{/* Grid layout for the images */}
			<div className="py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
				{images.map((image, index) => {
					// Set up individual item animation trigger
					// eslint-disable-next-line react-hooks/rules-of-hooks
					const { ref, inView } = useInView({
						triggerOnce: true,
						threshold: 0.5,
					})

					return (
						<div
							ref={ref} // Reference for each image item
							key={index}
							className={`relative ${
								inView ? 'animate-fade-in-up' : 'opacity-0'
							}`}
							onClick={() => openModal(image)}
						>
							<img
								src={image.image}
								alt={image.title}
								className="w-full h-80 object-cover rounded-lg cursor-pointer"
							/>
							<div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg hover:bg-opacity-70 transition-all duration-300 flex flex-col items-center justify-center gap-4 opacity-0 hover:opacity-100">
								<h3 className="text-white text-lg font-bold">{image.title}</h3>
								<p className="text-white text-sm">{image.description}</p>
							</div>
						</div>
					)
				})}
			</div>

			{/* Modal */}
			{isModalOpen && selectedImage && (
				<div className="fixed max-w-full max-h-full inset-0 bg-black bg-opacity-80 flex justify-center items-center z-20">
					<div className="relative w-full max-w-[90vw] max-h-full overflow-auto">
						{/* Image container with relative positioning */}
						<div className="relative w-full h-auto flex justify-center">
							{/* Image */}
							<img
								src={selectedImage.image}
								alt={selectedImage.title}
								className="w-full h-auto max-h-[70vh] object-cover rounded-lg"
							/>
						</div>

						{/* Modal content (description and title) */}
						<div className="bg-slate-600 px-16 bg-opacity-15 p-4 rounded-lg mt-4">
							<div className="text-center">
								<h3 className="text-white text-2xl font-bold">
									{selectedImage.title}
								</h3>
								<p className="text-white text-lg mt-2">
									{selectedImage.description}
								</p>
							</div>
						</div>

						{/* Close button */}
						<div className="mt-4 flex justify-center w-full">
							<button
								onClick={closeModal}
								className="text-white text-lg font-semibold underline hover:text-slate-400 transition-all duration-300"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default Gallery
